"use strict";

exports.__esModule = true;
exports.safeImageUrl = safeImageUrl;
exports.safeUrl = safeUrl;
exports.safeWindowOpen = safeWindowOpen;
/**
 * URL scheme allowlist (VAPT-02 / EXTRA-1).
 *
 * A `javascript:` URI in an href, or passed to window.open, executes with the
 * full privileges of the logged-in user. Several URLs in this application come
 * from stored data -- knowledge-base links are authored by one user and
 * rendered to others -- so they cannot be trusted.
 *
 * Validate the SCHEME, never a substring. Blocklisting the text "javascript:"
 * is defeated by casing, whitespace, tabs, entities and percent-encoding, so
 * the value is normalised before the scheme is read.
 *
 * Usage:
 *
 *   const url = safeUrl(item.url);
 *   {url ? <a href={url} rel="noopener noreferrer">{label}</a> : <span>{label}</span>}
 *
 *   const url = safeUrl(notification.link);
 *   if (url) window.open(url, "_blank", "noopener,noreferrer");
 */

var ALLOWED_SCHEMES = ["http:", "https:", "mailto:"];

// Any leading token of the form scheme: -- per RFC 3986.
var SCHEME_PATTERN = /^([a-z][a-z0-9+.-]*):/;

// Characters browsers strip or ignore when parsing a URL. Removing them before
// reading the scheme stops "java\tscript:" and " javascript:" slipping through.
// Built from an escaped string so no literal control bytes appear in source.
var IGNORED_PATTERN = new RegExp("[\\u0000-\\u0020\\u007F]", "g");
var NAMED_ENTITIES = {
  "&colon;": ":",
  "&tab;": "",
  "&newline;": "",
  "&sol;": "/"
};

/**
 * Decodes the entity forms an attacker might use to hide a scheme. Only ever
 * applied to the copy used for testing -- never to the value that is returned.
 */
function decodeForTesting(value) {
  var decoded = value.toLowerCase();
  Object.keys(NAMED_ENTITIES).forEach(function (entity) {
    decoded = decoded.split(entity).join(NAMED_ENTITIES[entity]);
  });

  // Numeric entities, decimal and hexadecimal: &#58; &#x3a;
  decoded = decoded.replace(/&#x([0-9a-f]+);?/g, function (_, hex) {
    return String.fromCharCode(parseInt(hex, 16));
  });
  decoded = decoded.replace(/&#([0-9]+);?/g, function (_, dec) {
    return String.fromCharCode(parseInt(dec, 10));
  });

  // Percent-encoding, so %6a%61%76%61script: is seen as javascript:
  try {
    decoded = decodeURIComponent(decoded);
  } catch (e) {
    // Malformed sequences are left as-is; the scheme check below still runs.
  }
  return decoded.replace(IGNORED_PATTERN, "");
}

/**
 * Returns the URL when its scheme is allowed, otherwise undefined so the caller
 * can render an inert element.
 *
 * Relative URLs (paths, query strings and fragments) carry no scheme and are
 * allowed. Never throws: one malformed value in a data set must not blank a
 * screen.
 */
function safeUrl(raw) {
  if (typeof raw !== "string") {
    return undefined;
  }
  var trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  var match = SCHEME_PATTERN.exec(decodeForTesting(trimmed));

  // No scheme means a relative URL, which cannot execute script.
  if (!match) {
    return trimmed;
  }
  return ALLOWED_SCHEMES.indexOf(match[1] + ":") !== -1 ? trimmed : undefined;
}

// Inline image payloads. Scripts never execute for an image loaded through an
// <img> element -- including SVG, where the spec disables scripting in that
// context -- so these are safe as an image source, and only as an image source.
var DATA_IMAGE_PATTERN = /^data:image\/[a-z0-9.+-]+;/i;

/**
 * Scheme check for image sources.
 *
 * Same rules as safeUrl, plus `data:image/...`, which the knowledge-base editor
 * relies on: images under 2 MB are inlined as base64 data URIs when inserted
 * (see MediaButton `handleImageInsert`). Using plain safeUrl here would stop
 * every such image rendering.
 *
 * Use this ONLY for image `src`. Never for `href`, `window.open` or an <embed>,
 * where `data:` can carry a document and execute script.
 */
function safeImageUrl(raw) {
  if (typeof raw !== "string") {
    return undefined;
  }
  var trimmed = raw.trim();
  if (DATA_IMAGE_PATTERN.test(trimmed.replace(IGNORED_PATTERN, ""))) {
    return trimmed;
  }
  return safeUrl(trimmed);
}

/**
 * Opens a URL in a new tab only when its scheme is allowed. Returns whether the
 * window was opened, so callers can surface an error if they need to.
 */
function safeWindowOpen(raw, target) {
  if (target === void 0) {
    target = "_blank";
  }
  var url = safeUrl(raw);
  if (!url) {
    return false;
  }
  window.open(url, target, "noopener,noreferrer");
  return true;
}